import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Badge, Modal, LoadingSkeleton } from './ui';
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, ClockIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../utils/roles';
import { safeErrorMessage } from '../utils/apiError';

const MANAGE_ROLES = ['administrator', 'principal'];

const CATEGORIES = [
  { value: 'general', label: 'General', variant: 'default' },
  { value: 'academic', label: 'Academic', variant: 'info' },
  { value: 'sports', label: 'Sports', variant: 'success' },
  { value: 'holiday', label: 'Holiday', variant: 'warning' },
  { value: 'meeting', label: 'Meeting', variant: 'primary' },
  { value: 'exam', label: 'Exam', variant: 'danger' },
];
const categoryInfo = (value) => CATEGORIES.find((c) => c.value === value) || CATEGORIES[0];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDateKey(date) {
  // Format using LOCAL date components, not toISOString() (which converts
  // to UTC first and shifts the date back a day for any positive UTC offset).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

const emptyForm = { title: '', description: '', category: 'general', start_date: '', end_date: '', start_time: '', end_time: '', location: '' };

function EventCalendar({ token, user }) {
  const canManage = hasAnyRole(user, MANAGE_ROLES);
  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const monthEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ start_date: toDateKey(gridStart), end_date: toDateKey(gridEnd) });
      const response = await fetch(`/api/v1/events?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setEvents(await response.json());
      } else {
        setError('Failed to load events');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, viewDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = ev.start_date;
      const end = ev.end_date || ev.start_date;
      const cur = new Date(start + 'T00:00:00');
      const last = new Date(end + 'T00:00:00');
      while (cur <= last) {
        const key = toDateKey(cur);
        (map[key] = map[key] || []).push(ev);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  const upcoming = useMemo(() => {
    const todayKey = toDateKey(today);
    return events.filter((ev) => ev.start_date >= todayKey).sort((a, b) => a.start_date.localeCompare(b.start_date)).slice(0, 8);
  }, [events, today]);

  const days = [];
  for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }

  const openCreateForm = (dateKey) => {
    setEditingEvent(null);
    setFormData({ ...emptyForm, start_date: dateKey || toDateKey(today) });
    setShowForm(true);
  };

  const openEditForm = (ev) => {
    setEditingEvent(ev);
    setFormData({
      title: ev.title, description: ev.description || '', category: ev.category || 'general',
      start_date: ev.start_date, end_date: ev.end_date || '', start_time: ev.start_time || '',
      end_time: ev.end_time || '', location: ev.location || ''
    });
    setSelectedDay(null);
    setShowForm(true);
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      const response = await fetch(`/api/v1/events/${ev.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setEvents((prev) => prev.filter((e) => e.id !== ev.id));
        setSelectedDay(null);
      } else {
        setError(await safeErrorMessage(response, 'Failed to delete event'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const url = editingEvent ? `/api/v1/events/${editingEvent.id}` : '/api/v1/events';
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formData, end_date: formData.end_date || null, start_time: formData.start_time || null, end_time: formData.end_time || null }),
      });
      if (response.ok) {
        setShowForm(false);
        fetchEvents();
      } else {
        setError(await safeErrorMessage(response, `Failed to ${editingEvent ? 'update' : 'create'} event`));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedDayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];
  const todayKey = toDateKey(today);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Event Calendar</h2>
        {canManage && (
          <Button variant="primary" size="sm" onClick={() => openCreateForm(null)}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add Event
          </Button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button variant="ghost" size="sm" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}>
              <ChevronLeftIcon style={{ width: 16, height: 16 }} />
            </Button>
            <span style={{ fontWeight: 600, minWidth: 150, textAlign: 'center' }}>
              {viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}>
              <ChevronRightIcon style={{ width: 16, height: 16 }} />
            </Button>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1))}>
            Today
          </Button>
        </div>

        {loading ? (
          <LoadingSkeleton type="text" rows={6} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: 'var(--color-border)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {WEEKDAYS.map((wd) => (
              <div key={wd} style={{ background: 'var(--color-bg)', padding: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-secondary)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                {wd}
              </div>
            ))}
            {days.map((day) => {
              const key = toDateKey(day);
              const dayEvents = eventsByDay[key] || [];
              const isCurrentMonth = day.getMonth() === viewDate.getMonth();
              const isToday = key === todayKey;
              return (
                <div
                  key={key}
                  onClick={() => setSelectedDay(key)}
                  style={{
                    background: 'var(--color-surface)', minHeight: 90, minWidth: 0, padding: '0.375rem', cursor: 'pointer',
                    opacity: isCurrentMonth ? 1 : 0.45, display: 'flex', flexDirection: 'column', gap: '0.25rem', overflow: 'hidden',
                  }}
                >
                  <span style={{
                    fontSize: '0.8rem', fontWeight: isToday ? 700 : 500, color: isToday ? 'white' : 'var(--color-text)',
                    background: isToday ? 'var(--color-primary)' : 'transparent',
                    width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                  }}>
                    {day.getDate()}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    {dayEvents.slice(0, 2).map((ev) => (
                      <div key={ev.id} title={ev.title} style={{ overflow: 'hidden', minWidth: 0 }}>
                        <Badge variant={categoryInfo(ev.category).variant} className="calendarPill">
                          {ev.title}
                        </Badge>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>+{dayEvents.length - 2} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div style={{ marginTop: '1.5rem' }}>
        <Card title="Upcoming Events">
          {upcoming.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1.5rem' }}>No upcoming events.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {upcoming.map((ev) => (
                <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: '0.9rem' }}>{ev.title}</strong>
                      <Badge variant={categoryInfo(ev.category).variant}>{categoryInfo(ev.category).label}</Badge>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <span>{new Date(ev.start_date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      {ev.start_time && <span><ClockIcon style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle' }} /> {formatTime(ev.start_time)}</span>}
                      {ev.location && <span><MapPinIcon style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle' }} /> {ev.location}</span>}
                    </div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      <Button variant="ghost" size="sm" onClick={() => openEditForm(ev)}><PencilIcon style={{ width: 14, height: 14 }} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ev)}><TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Modal isOpen={Boolean(selectedDay)} onClose={() => setSelectedDay(null)} title={selectedDay ? new Date(selectedDay + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ''}>
        {selectedDayEvents.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No events on this day.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: canManage ? '1rem' : 0 }}>
            {selectedDayEvents.map((ev) => (
              <div key={ev.id} style={{ padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <strong>{ev.title}</strong>
                      <Badge variant={categoryInfo(ev.category).variant}>{categoryInfo(ev.category).label}</Badge>
                    </div>
                    {(ev.start_time || ev.location) && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'flex', gap: '1rem', marginBottom: '0.25rem' }}>
                        {ev.start_time && <span>{formatTime(ev.start_time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ''}</span>}
                        {ev.location && <span>{ev.location}</span>}
                      </div>
                    )}
                    {ev.description && <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{ev.description}</p>}
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
                      <Button variant="ghost" size="sm" onClick={() => openEditForm(ev)}><PencilIcon style={{ width: 14, height: 14 }} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ev)}><TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {canManage && (
          <Button variant="primary" size="sm" onClick={() => openCreateForm(selectedDay)}>
            <PlusIcon style={{ width: 14, height: 14 }} />
            Add Event on This Day
          </Button>
        )}
      </Modal>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingEvent ? 'Edit Event' : 'Add Event'}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g. Main Hall" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>End Date (optional, for multi-day events)</label>
              <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} min={formData.start_date} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Time (optional)</label>
              <input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} />
            </div>
            <div className="form-group">
              <label>End Time (optional)</label>
              <input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : (editingEvent ? 'Save Changes' : 'Create Event')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default EventCalendar;
